import { useTranslation } from 'react-i18next'
import {
  COLORS,
  FIXED_OPTIONS,
  GENDERS,
  type FixedOption,
  type Gender,
} from '../lib/constants'

export type ChipStatus = 'unknown' | 'none' | 'known'
export type ChipLabelContext = 'scanner' | 'owner'

interface Props {
  idPrefix: string
  color1: string
  color2: string
  color3: string
  gender: Gender
  fixed: FixedOption
  chipStatus: ChipStatus
  chip: string
  onColor1: (value: string) => void
  onColor2: (value: string) => void
  onColor3: (value: string) => void
  onGender: (value: Gender) => void
  onFixed: (value: FixedOption) => void
  onChipStatus: (value: ChipStatus) => void
  onChip: (value: string) => void
  /** Owner reports use shorter labels; sightings keep scanner-oriented wording. */
  chipLabelContext?: ChipLabelContext
}

export function AnimalReportFields({
  idPrefix,
  color1,
  color2,
  color3,
  gender,
  fixed,
  chipStatus,
  chip,
  onColor1,
  onColor2,
  onColor3,
  onGender,
  onFixed,
  onChipStatus,
  onChip,
  chipLabelContext = 'scanner',
}: Props) {
  const { t } = useTranslation()
  const chipKey = chipLabelContext === 'owner' ? 'chip.owner' : 'chip'

  function colorOptions(includeSelect: boolean) {
    return (
      <>
        <option value="">{includeSelect ? t('fields.selectOne') : t('fields.none')}</option>
        {COLORS.map((key) => (
          <option key={key} value={key}>
            {t(`colors.${key}`)}
          </option>
        ))}
      </>
    )
  }

  return (
    <>
      <div className="color-grid">
        <div className="field">
          <label htmlFor={`${idPrefix}-color1`}>{t('fields.color1')}</label>
          <select
            id={`${idPrefix}-color1`}
            value={color1}
            onChange={(event) => onColor1(event.target.value)}
            required
          >
            {colorOptions(true)}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-color2`}>{t('fields.color2')}</label>
          <select
            id={`${idPrefix}-color2`}
            value={color2}
            onChange={(event) => onColor2(event.target.value)}
          >
            {colorOptions(false)}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-color3`}>{t('fields.color3')}</label>
          <select
            id={`${idPrefix}-color3`}
            value={color3}
            onChange={(event) => onColor3(event.target.value)}
          >
            {colorOptions(false)}
          </select>
        </div>
      </div>

      <fieldset className="chip-fieldset">
        <legend>{t('fields.gender')}</legend>
        <div className="segmented-options">
          {GENDERS.map((value) => (
            <label key={value}>
              <input
                type="radio"
                name={`${idPrefix}-gender`}
                value={value}
                checked={gender === value}
                onChange={() => onGender(value)}
              />
              <span>{t(`gender.${value}`)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="chip-fieldset">
        <legend>{t('fields.fixed')}</legend>
        <div className="segmented-options">
          {FIXED_OPTIONS.map((value) => (
            <label key={value}>
              <input
                type="radio"
                name={`${idPrefix}-fixed`}
                value={value}
                checked={fixed === value}
                onChange={() => onFixed(value)}
              />
              <span>{t(`fixed.${value}`)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="chip-fieldset">
        <legend>{t('fields.chip')}</legend>
        <p className="field-help">
          {t(
            chipLabelContext === 'owner'
              ? 'forms.chipHelpOwner'
              : 'forms.chipHelp',
          )}
        </p>
        <div className="segmented-options">
          {(['unknown', 'none', 'known'] as const).map((status) => (
            <label key={status}>
              <input
                type="radio"
                name={`${idPrefix}-chip-status`}
                value={status}
                checked={chipStatus === status}
                onChange={() => onChipStatus(status)}
              />
              <span>{t(`${chipKey}.${status}`)}</span>
            </label>
          ))}
        </div>
        {chipStatus === 'known' && (
          <div className="field chip-number">
            <label htmlFor={`${idPrefix}-chip`}>{t('chip.number')}</label>
            <input
              id={`${idPrefix}-chip`}
              value={chip}
              onChange={(event) =>
                onChip(event.target.value.replace(/\D/g, '').slice(0, 15))
              }
              inputMode="numeric"
              pattern="[0-9]{15}"
              minLength={15}
              maxLength={15}
              placeholder={t('forms.chipPlaceholder')}
              required
            />
            <small>{t('chip.formatHelp')}</small>
          </div>
        )}
      </fieldset>
    </>
  )
}
